import { Add as AddIcon } from "@mui/icons-material";
import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { useProductSearch } from "../../hooks/queries/useProductSearch";
import { ProductCard } from "../../types/productPlane";
import { productLevelAndStatus, productName } from "../Explore/productFields";
import { MAX_COMPARED } from "./compareState";

// The most products the field offers for one text
const OPTIONS_SHOWN = 8;

// How long the typing rests before the search is asked, in milliseconds
const SEARCH_DELAY = 250;

// What the field says once six products are compared
export const FULL_HINT = "Six products at most: remove one to add another.";

interface AddProductFieldProps {
  // The ids of the products compared: they are not offered again
  compared: string[];
  // Called with the product chosen
  onAdd: (product: ProductCard) => void;
}

// The field that adds a product to the comparison: it searches products by
// the text typed, with the product search of Explore, and adds the one
// chosen. With six products compared it is disabled and says why.
const AddProductField: React.FC<AddProductFieldProps> = ({
  compared,
  onAdd,
}) => {
  const [text, setText] = useState("");
  const [keyword, setKeyword] = useState("");

  // The search follows the typing once it rests
  useEffect(() => {
    const timer = setTimeout(() => setKeyword(text.trim()), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [text]);

  const full = compared.length >= MAX_COMPARED;

  // Enough results to offer eight after the compared ones are left out
  const search = useProductSearch(
    useMemo(
      () => ({ keyword, limit: OPTIONS_SHOWN + compared.length }),
      [keyword, compared.length],
    ),
    keyword !== "" && !full,
  );

  const options = useMemo(
    () =>
      keyword === ""
        ? []
        : (search.data?.results.items ?? [])
            .filter((product: ProductCard) => !compared.includes(product.id))
            .slice(0, OPTIONS_SHOWN),
    [keyword, search.data, compared],
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
      <Autocomplete<ProductCard>
        size="small"
        disabled={full}
        // Nothing stays chosen: a choice is added and the field emptied
        value={null}
        inputValue={text}
        onInputChange={(_event, value, reason) =>
          setText(reason === "reset" ? "" : value)
        }
        onChange={(_event, product) => {
          if (product) {
            onAdd(product);
            setText("");
          }
        }}
        options={options}
        // The search has matched the text already
        filterOptions={(found) => found}
        getOptionLabel={productName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={search.isFetching}
        noOptionsText={
          text.trim() === ""
            ? "Type the name of a product"
            : search.error
              ? "The search failed"
              : "No product found"
        }
        blurOnSelect
        clearOnBlur
        forcePopupIcon={false}
        sx={{ width: 260 }}
        renderOption={(props, product) => (
          <Box component="li" {...props} key={product.id}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                {productName(product)}
              </Box>
              <Box sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {productLevelAndStatus(product)}
              </Box>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Add a product (up to ${MAX_COMPARED})`}
            inputProps={{
              ...params.inputProps,
              "aria-label": "Add a product",
              maxLength: 200,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <AddIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { fontSize: 13 },
            }}
          />
        )}
      />
      {full && (
        <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
          {FULL_HINT}
        </Box>
      )}
    </Box>
  );
};

export default AddProductField;
